import axios from 'axios';

// ========== Dashboard Data (reads from DB) ==========

export async function fetchExecutiveSummary(dateFrom, dateTo) {
  try {
    const res = await axios.get('/api/executive/summary', {
      params: { date_from: dateFrom, date_to: dateTo },
    });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

// ========== AI Report ==========

export async function generateAIReport(dateFrom, dateTo) {
  try {
    const res = await axios.post('/api/executive/ai-report', {
      date_from: dateFrom,
      date_to: dateTo,
    });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

// ========== Google Ads ==========

export async function fetchGoogleAdsCampaigns(dateFrom, dateTo) {
  try {
    const res = await axios.get('/api/google-ads/campaigns', {
      params: { date_from: dateFrom, date_to: dateTo },
    });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message, campaigns: [] };
  }
}

export async function fetchGoogleAdsKeywords(dateFrom, dateTo) {
  try {
    const res = await axios.get('/api/google-ads/keywords', { params: { date_from: dateFrom, date_to: dateTo } });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message, keywords: [] };
  }
}

export async function fetchGoogleAdsAdGroups(dateFrom, dateTo) {
  try {
    const res = await axios.get('/api/google-ads/adgroups', { params: { date_from: dateFrom, date_to: dateTo } });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message, adGroups: [] };
  }
}

// ========== CRM Leads ==========

export async function fetchLeads(params = {}) {
  try {
    const res = await axios.get('/api/leads', { params });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message, leads: [] };
  }
}

export async function fetchLeadStats() {
  try {
    const res = await axios.get('/api/leads/stats');
    return res.data;
  } catch (err) {
    return { error: err.message };
  }
}

export async function fetchSalesTeam() {
  try {
    const res = await axios.get('/api/leads/team');
    return res.data;
  } catch (err) {
    return { team: [] };
  }
}

export async function createLead(lead) {
  try {
    const res = await axios.post('/api/leads', lead);
    return res.data;
  } catch (err) {
    const d = err.response?.data || {};
    return { error: d.error || err.message, duplicate: d.duplicate, message: d.message, history: d.history };
  }
}

export async function bulkImportLeads(leads) {
  try {
    const res = await axios.post('/api/leads/bulk-import', { leads });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

// ── Ad Campaign Composer ──
export async function fetchCampaigns() {
  try { const res = await axios.get('/api/campaigns'); return res.data; }
  catch (err) { return { error: err.response?.data?.error || err.message, campaigns: [] }; }
}
export async function saveCampaign(id, data) {
  try {
    const res = id ? await axios.put(`/api/campaigns/${id}`, data) : await axios.post('/api/campaigns', data);
    return res.data;
  } catch (err) { return { error: err.response?.data?.error || err.message }; }
}
export async function deleteCampaign(id) {
  try { const res = await axios.delete(`/api/campaigns/${id}`); return res.data; }
  catch (err) { return { error: err.response?.data?.error || err.message }; }
}
export async function duplicateCampaign(id) {
  try { const res = await axios.post(`/api/campaigns/${id}/duplicate`); return res.data; }
  catch (err) { return { error: err.response?.data?.error || err.message }; }
}
export async function publishCampaign(id) {
  try { const res = await axios.post(`/api/campaigns/${id}/publish`); return res.data; }
  catch (err) { return { error: err.response?.data?.error || err.message }; }
}
export async function fetchLiveCampaigns(platform = 'tiktok') {
  try { const res = await axios.get('/api/campaigns/live', { params: { platform } }); return res.data; }
  catch (err) { return { ok: false, error: err.response?.data?.error || err.message, campaigns: [] }; }
}
// Enable the whole hierarchy (campaign + ad sets + ads) so it actually delivers.
export async function enableCampaignChain(id, platform = 'tiktok') {
  try { const res = await axios.post(`/api/campaigns/live/${id}/enable-chain`, { platform }); return res.data; }
  catch (err) { return { error: err.response?.data?.error || err.message }; }
}
export async function setLiveCampaignStatus(id, enable, platform = 'tiktok') {
  try { const res = await axios.post(`/api/campaigns/live/${id}/status`, { enable, platform }); return res.data; }
  catch (err) { return { error: err.response?.data?.error || err.message }; }
}
export async function sourceDraftForLive(campaignId) {
  try { const res = await axios.get(`/api/campaigns/source-draft/${campaignId}`); return res.data; }
  catch (err) { return { error: err.response?.data?.error || err.message }; }
}
export async function reconstructLive(campaignId, platform = 'tiktok') {
  try { const res = await axios.get(`/api/campaigns/live/${campaignId}/reconstruct`, { params: { platform } }); return res.data; }
  catch (err) { return { error: err.response?.data?.error || err.message }; }
}
export async function updateLiveCampaign(id, fields, platform = 'tiktok') {
  try { const res = await axios.post(`/api/campaigns/live/${id}/update`, { ...fields, platform }); return res.data; }
  catch (err) { return { error: err.response?.data?.error || err.message }; }
}
export async function fetchLiveCampaignDetail(id, platform = 'tiktok') {
  try { const res = await axios.get(`/api/campaigns/live/${id}/detail`, { params: { platform } }); return res.data; }
  catch (err) { return { ok: false, error: err.response?.data?.error || err.message }; }
}
// Snap serves videos with a wrong content-type, so a raw <video src> can't play the
// /snap-media proxy path; fetch it as an authed blob and hand back a video/mp4 object URL.
export async function campaignVideoBlobUrl(path) {
  const res = await axios.get(path, { responseType: 'blob' });
  return URL.createObjectURL(new Blob([res.data], { type: 'video/mp4' }));
}
export async function setLiveAdgroupStatus(id, enable, platform, campaignId) {
  try { const res = await axios.post(`/api/campaigns/live/adgroup/${id}/status`, { enable, platform, campaignId }); return res.data; }
  catch (err) { return { ok: false, error: err.response?.data?.error || err.message }; }
}
export async function updateLiveAdgroup(id, budget, platform, campaignId) {
  try { const res = await axios.post(`/api/campaigns/live/adgroup/${id}/update`, { budget, platform, campaignId }); return res.data; }
  catch (err) { return { ok: false, error: err.response?.data?.error || err.message }; }
}
// ── Snap lead-form → CRM wiring (admin) ──
export async function fetchSnapLeadForms() {
  try { const res = await axios.get('/api/snap-leads/forms'); return res.data; }
  catch (err) { return { ok: false, error: err.response?.data?.error || err.message, forms: [] }; }
}
export async function connectSnapLeadForm(formId, body) {
  try { const res = await axios.post(`/api/snap-leads/forms/${formId}/connect`, body); return res.data; }
  catch (err) { return { ok: false, error: err.response?.data?.error || err.message }; }
}
export async function disconnectSnapLeadForm(formId, body) {
  try { const res = await axios.post(`/api/snap-leads/forms/${formId}/disconnect`, body); return res.data; }
  catch (err) { return { ok: false, error: err.response?.data?.error || err.message }; }
}
export async function setSnapLeadFormOwner(formId, body) {
  try { const res = await axios.patch(`/api/snap-leads/forms/${formId}`, body); return res.data; }
  catch (err) { return { ok: false, error: err.response?.data?.error || err.message }; }
}
export async function setLiveAdStatus(id, enable, platform, adgroupId) {
  try { const res = await axios.post(`/api/campaigns/live/ad/${id}/status`, { enable, platform, adgroupId }); return res.data; }
  catch (err) { return { ok: false, error: err.response?.data?.error || err.message }; }
}
export async function updateLiveAd(id, fields, platform) {
  try { const res = await axios.post(`/api/campaigns/live/ad/${id}/update`, { ...fields, platform }); return res.data; }
  catch (err) { return { ok: false, error: err.response?.data?.error || err.message }; }
}
export async function fetchLeadForms(platform = 'tiktok') {
  try { const res = await axios.get('/api/campaigns/lead-forms', { params: { platform } }); return res.data; }
  catch (err) { return { ok: false, error: err.response?.data?.error || err.message, forms: [] }; }
}
export async function geocodeArea(lat, lng) {
  try { const res = await axios.get('/api/campaigns/geocode', { params: { lat, lng } }); return res.data; }
  catch (err) { return { error: err.response?.data?.error || err.message }; }
}
export async function fetchTargeting(platform = 'tiktok', country) {
  try { const res = await axios.get('/api/campaigns/targeting', { params: { platform, country } }); return res.data; }
  catch (err) { return { ok: false, error: err.response?.data?.error || err.message, interests: [], regions: [] }; }
}
export async function uploadCampaignImage(file) {
  try {
    const res = await axios.post('/api/campaigns/upload-image', file, { headers: { 'Content-Type': file.type || 'application/octet-stream' } });
    return res.data;
  } catch (err) { return { error: err.response?.data?.error || err.message }; }
}
// Same endpoint accepts video too (stored as a BLOB, served by URL for the platforms).
export async function uploadCampaignVideo(file) {
  try {
    const res = await axios.post('/api/campaigns/upload-image', file, { headers: { 'Content-Type': file.type || 'application/octet-stream' } });
    return res.data;
  } catch (err) { return { error: err.response?.data?.error || err.message }; }
}
export async function checkTiktokCreativeAccess() {
  try { const res = await axios.get('/api/campaigns/tiktok/creative-access'); return res.data; }
  catch (err) { return { granted: false, error: err.response?.data?.error || err.message }; }
}

export async function dealRequestFormData() {
  try {
    const res = await axios.get('/api/deal-requests/form-data');
    return res.data;
  } catch (err) {
    return { units: [], brokers: [], team: [] };
  }
}

export async function listDealRequests() {
  try {
    const res = await axios.get('/api/deal-requests');
    return res.data;
  } catch (err) {
    return { requests: [], canApprove: false };
  }
}

export async function submitDealRequest(payload) {
  try {
    const res = await axios.post('/api/deal-requests', payload);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function approveDealRequest(id) {
  try {
    const res = await axios.post(`/api/deal-requests/${id}/approve`);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function rejectDealRequest(id, reason) {
  try {
    const res = await axios.post(`/api/deal-requests/${id}/reject`, { reason });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function brokerAddCustomer(payload) {
  try {
    const res = await axios.post('/api/broker/customers', payload);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function brokerSubmitDeal(payload) {
  try {
    const res = await axios.post('/api/broker/deal-requests', payload);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function brokerDealRequests() {
  try {
    const res = await axios.get('/api/broker/deal-requests');
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function brokerListEmployees() {
  try {
    const res = await axios.get('/api/broker/employees');
    return res.data;
  } catch (err) {
    return { employees: [], error: err.response?.data?.error || err.message };
  }
}

export async function brokerAddEmployee(payload) {
  try {
    const res = await axios.post('/api/broker/employees', payload);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function brokerToggleEmployee(id, active) {
  try {
    const res = await axios.patch(`/api/broker/employees/${id}/active`, { active });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function brokerResetEmployeePassword(id, password) {
  try {
    const res = await axios.patch(`/api/broker/employees/${id}/password`, { password });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function brokerUpdateEmployee(id, payload) {
  try {
    const res = await axios.patch(`/api/broker/employees/${id}`, payload);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function brokerDeleteEmployee(id) {
  try {
    const res = await axios.delete(`/api/broker/employees/${id}`);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function brokerSetEmployeePermissions(id, perms) {
  try {
    const res = await axios.patch(`/api/broker/employees/${id}/permissions`, { perms });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function brokerAnalytics(params = {}) {
  try {
    const res = await axios.get('/api/broker/analytics', { params });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

// Open (non-lost) leads a member still holds — checked before deactivating them.
export async function getAssigneeActiveLeads(name) {
  try {
    const res = await axios.get('/api/leads/by-assignee/active', { params: { name } });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message, count: 0, ids: [] };
  }
}

export async function bulkAssignLeads(leadIds, assignedTo) {
  try {
    const res = await axios.post('/api/leads/bulk-assign', { leadIds, assignedTo });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function brokerAssignLead(id, assignedTo) {
  try {
    const res = await axios.patch(`/api/broker/leads/${id}/assign`, { assigned_to: assignedTo });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function brokerMemberLeads(params = {}) {
  try {
    const res = await axios.get('/api/broker/member-leads', { params });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

// Admin/manager: performance across ALL broker companies (director-level).
export async function brokerMonitor(params = {}) {
  try {
    const res = await axios.get('/api/leads/broker-monitor', { params });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

// ── Broker-company management (الوسطاء tab): list + add + edit/disable + delete ──
export async function brokersList() {
  try {
    const res = await axios.get('/api/brokers');
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message, brokers: [] };
  }
}
export async function brokerCreate(body) {
  try {
    const res = await axios.post('/api/brokers', body);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}
export async function brokerUpdate(id, body) {
  try {
    const res = await axios.patch(`/api/brokers/${id}`, body);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}
export async function brokerDelete(id) {
  try {
    const res = await axios.delete(`/api/brokers/${id}`);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message, ...(err.response?.data || {}) };
  }
}

// ── Broker-company user accounts (admin, from the الوسطاء tab) ──
export async function brokerUsersList(id) {
  try {
    const res = await axios.get(`/api/brokers/${id}/users`);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message, users: [] };
  }
}
export async function brokerUserCreate(id, body) {
  try {
    const res = await axios.post(`/api/brokers/${id}/users`, body);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}
export async function brokerUserUpdate(id, uid, body) {
  try {
    const res = await axios.patch(`/api/brokers/${id}/users/${uid}`, body);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}
export async function brokerUserDelete(id, uid) {
  try {
    const res = await axios.delete(`/api/brokers/${id}/users/${uid}`);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

// ── Team task board (Notion-style) ──
export async function tasksList(params = {}) {
  try {
    const res = await axios.get('/api/tasks', { params });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message, tasks: [] };
  }
}
export async function taskGet(id) {
  try {
    const res = await axios.get(`/api/tasks/${id}`);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}
export async function taskAssignees() {
  try {
    const res = await axios.get('/api/tasks/meta/assignees');
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message, assignees: [] };
  }
}
export async function taskCreate(body) {
  try {
    const res = await axios.post('/api/tasks', body);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}
export async function taskUpdate(id, body) {
  try {
    const res = await axios.patch(`/api/tasks/${id}`, body);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}
export async function taskDelete(id) {
  try {
    const res = await axios.delete(`/api/tasks/${id}`);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}
export async function taskAddComment(id, message) {
  try {
    const res = await axios.post(`/api/tasks/${id}/comments`, { message });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

// Admin/manager: stagnant ("راكد") leads — active, no upcoming action scheduled.
export async function staleLeads(params = {}) {
  try {
    const res = await axios.get('/api/leads/stale-leads', { params });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message, leads: [], total: 0, members: [] };
  }
}

// ── Project media (images/videos per project; brokers view + download) ──
export async function projectMediaList(project) {
  try {
    const res = await axios.get('/api/project-media', { params: project ? { project } : {} });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message, media: [] };
  }
}

export async function projectMediaUpload(file, project, title, type) {
  try {
    const res = await axios.post('/api/project-media', file, {
      params: { project, title, type },
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function projectMediaDelete(id) {
  try {
    const res = await axios.delete(`/api/project-media/${id}`);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

// ── Lead questionnaire mapping (form question → CRM field) ──
export async function getLeadQuestionMap() {
  try { const res = await axios.get('/api/leads/question-map'); return res.data; }
  catch (err) { return { error: err.response?.data?.error || err.message, map: {}, defaults: {} }; }
}
export async function saveLeadQuestionMap(map) {
  try { const res = await axios.put('/api/leads/question-map', { map }); return res.data; }
  catch (err) { return { error: err.response?.data?.error || err.message }; }
}

// Fetch the file with the auth header and return an object URL (so <img>/<video>/
// download work without putting the JWT in the URL).
export async function projectMediaBlobUrl(id) {
  const res = await axios.get(`/api/project-media/${id}/file`, { responseType: 'blob' });
  return URL.createObjectURL(res.data);
}

// Distinct projects that actually have units (admin/manager) — the real
// inventory projects, used to tag project media.
export async function unitsProjectsList() {
  try {
    const res = await axios.get('/api/units-deals/units/projects');
    return res.data;
  } catch (err) {
    return { projects: [] };
  }
}

// Create a project with no units yet (name only).
export async function createProject(name) {
  try { const res = await axios.post('/api/units-deals/projects', { name }); return res.data; }
  catch (err) { return { error: err.response?.data?.error || err.message }; }
}

// Rename a project (cascades to its units/deals/media on the server).
export async function renameProject(oldName, newName) {
  try { const res = await axios.put(`/api/units-deals/projects/${encodeURIComponent(oldName)}`, { name: newName }); return res.data; }
  catch (err) { return { error: err.response?.data?.error || err.message }; }
}

// Delete a project + its available units (server blocks if any unit has a deal).
export async function deleteProject(name) {
  try { const res = await axios.delete(`/api/units-deals/projects/${encodeURIComponent(name)}?confirm=1`); return res.data; }
  catch (err) { return { error: err.response?.data?.error || err.message, ...(err.response?.data || {}) }; }
}

// Delete a single unit (server blocks if it has a deal/reservation).
export async function deleteUnit(id) {
  try { const res = await axios.delete(`/api/units-deals/units/${id}`); return res.data; }
  catch (err) { return { error: err.response?.data?.error || err.message }; }
}

// My deal-closure tasks — deals with checklist steps assigned to the current member.
export async function getClosureTasks() {
  try {
    const res = await axios.get('/api/closure-tasks');
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message, deals: [] };
  }
}

export async function updateClosureTask(unitId, index, value) {
  try {
    const res = await axios.patch(`/api/closure-tasks/${unitId}`, { index, value });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

// Distinct project names from inventory (الوحدات), for the lead form's project dropdown.
// Available to any lead creator (unlike unitsProjectsList, which needs page.units_deals).
export async function leadProjectsList() {
  try {
    const res = await axios.get('/api/leads/projects');
    return res.data;
  } catch (err) {
    return { projects: [] };
  }
}

// ── Deal-closure checklist assignees (company-wide step → employee) ──
export async function getChecklistAssignees() {
  try {
    const res = await axios.get('/api/units-deals/checklist-assignees');
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message, assignees: {} };
  }
}

export async function saveChecklistAssignees(assignees) {
  try {
    const res = await axios.put('/api/units-deals/checklist-assignees', { assignees });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

// ── Building maintenance (work-orders) ──
export async function maintenanceIssues(params = {}) {
  try { const res = await axios.get('/api/maintenance/issues', { params }); return res.data; }
  catch (err) { return { error: err.response?.data?.error || err.message, issues: [], total: 0 }; }
}
export async function maintenanceIssue(id) {
  try { const res = await axios.get(`/api/maintenance/issues/${id}`); return res.data; }
  catch (err) { return { error: err.response?.data?.error || err.message }; }
}
export async function maintenanceStats(project) {
  try { const res = await axios.get('/api/maintenance/stats', { params: project ? { project } : {} }); return res.data; }
  catch (err) { return { error: err.response?.data?.error || err.message, byStatus: {} }; }
}
export async function createMaintenanceIssue(body) {
  try { const res = await axios.post('/api/maintenance/issues', body); return res.data; }
  catch (err) { return { error: err.response?.data?.error || err.message }; }
}
export async function updateMaintenanceIssue(id, patch) {
  try { const res = await axios.patch(`/api/maintenance/issues/${id}`, patch); return res.data; }
  catch (err) { return { error: err.response?.data?.error || err.message }; }
}
export async function maintenanceComment(id, note) {
  try { const res = await axios.post(`/api/maintenance/issues/${id}/comment`, { note }); return res.data; }
  catch (err) { return { error: err.response?.data?.error || err.message }; }
}
export async function maintenanceUploadMedia(id, file, caption) {
  try {
    const res = await axios.post(`/api/maintenance/issues/${id}/media`, file, {
      params: caption ? { caption } : {},
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    });
    return res.data;
  } catch (err) { return { error: err.response?.data?.error || err.message }; }
}
export async function maintenanceMediaBlobUrl(mediaId) {
  const res = await axios.get(`/api/maintenance/media/${mediaId}/file`, { responseType: 'blob' });
  return URL.createObjectURL(res.data);
}
export async function deleteMaintenanceIssue(id) {
  try { const res = await axios.delete(`/api/maintenance/issues/${id}`); return res.data; }
  catch (err) { return { error: err.response?.data?.error || err.message }; }
}
export async function maintenanceTeam(project) {
  try { const res = await axios.get('/api/maintenance/team', { params: project ? { project } : {} }); return res.data; }
  catch (err) { return { error: err.response?.data?.error || err.message, members: [], teams: {} }; }
}
export async function saveMaintenanceTeam(project, members) {
  try { const res = await axios.put('/api/maintenance/team', { project, members }); return res.data; }
  catch (err) { return { error: err.response?.data?.error || err.message }; }
}

// ── Broker commissions (admin/manager ledger + receipts) ──
export async function commissionsList(params = {}) {
  try {
    const res = await axios.get('/api/commissions', { params });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message, commissions: [], totals: {} };
  }
}

export async function commissionSetStatus(dealId, status, note) {
  try {
    const res = await axios.patch(`/api/commissions/${dealId}`, { status, note });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function commissionUploadReceipt(dealId, file) {
  try {
    const res = await axios.post(`/api/commissions/${dealId}/receipt`, file, {
      params: { name: file.name },
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function commissionDeleteReceipt(dealId) {
  try {
    const res = await axios.delete(`/api/commissions/${dealId}/receipt`);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

// Fetch the receipt with the auth header → object URL (no JWT in the URL).
export async function commissionReceiptBlobUrl(dealId) {
  const res = await axios.get(`/api/commissions/${dealId}/receipt`, { responseType: 'blob' });
  return URL.createObjectURL(res.data);
}

// ── Broker-side commissions (company-scoped; broker.viewFinancials) ──
export async function brokerCommissions() {
  try {
    const res = await axios.get('/api/broker/commissions');
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message, commissions: [], totals: {} };
  }
}

export async function brokerCommissionReceiptBlobUrl(dealId) {
  const res = await axios.get(`/api/broker/commissions/${dealId}/receipt`, { responseType: 'blob' });
  return URL.createObjectURL(res.data);
}

export async function updateLead(id, lead) {
  try {
    const res = await axios.put(`/api/leads/${id}`, lead);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function patchLeadStatus(id, status) {
  try {
    const res = await axios.patch(`/api/leads/${id}/status`, { status });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function toggleLeadImportant(id, important) {
  try {
    const res = await axios.patch(`/api/leads/${id}/important`, { important: important ? 1 : 0 });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function deleteLead(id) {
  try {
    const res = await axios.delete(`/api/leads/${id}`);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function transitionLeadStatus(leadId, payload) {
  try {
    const res = await axios.post(`/api/leads/${leadId}/transition`, payload);
    return res.data;
  } catch (err) {
    const d = err.response?.data || {};
    // Surface daily-limit details so callers can offer a bypass.
    return { error: d.error || err.message, limit: d.limit, count: d.count, date: d.date };
  }
}

export async function sendDirective(leadId, message, assignedTo) {
  try {
    const res = await axios.post(`/api/leads/${leadId}/directive`, { message, assignedTo });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function replyDirective(leadId, message) {
  try {
    const res = await axios.post(`/api/leads/${leadId}/directive-reply`, { message });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function reopenLead(leadId, note) {
  try {
    const res = await axios.post(`/api/leads/${leadId}/reopen`, { note });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function fetchLeadTransitions(leadId) {
  try {
    const res = await axios.get(`/api/leads/${leadId}/transitions`);
    return res.data;
  } catch (err) {
    return [];
  }
}

export async function addCommunication(leadId, entry) {
  try {
    const res = await axios.post(`/api/leads/${leadId}/communications`, entry);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

// ========== AI Assistant ==========

export async function getAssistantStatus() {
  try {
    const res = await axios.get('/api/assistant/status');
    return res.data;
  } catch (err) {
    return { configured: false, error: err.response?.data?.error || err.message };
  }
}

export async function askAssistant(messages, conversationId) {
  try {
    const res = await axios.post('/api/assistant', { messages, conversationId });
    return res.data;
  } catch (err) {
    const d = err.response?.data || {};
    return { error: d.error || err.message, status: err.response?.status };
  }
}

export async function listAssistantConversations() {
  try {
    const res = await axios.get('/api/assistant/conversations');
    return res.data;
  } catch (err) {
    return { conversations: [], error: err.response?.data?.error || err.message };
  }
}

export async function getAssistantConversation(id) {
  try {
    const res = await axios.get(`/api/assistant/conversations/${id}`);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function deleteAssistantConversation(id) {
  try {
    const res = await axios.delete(`/api/assistant/conversations/${id}`);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function getAssistantConfig() {
  try {
    const res = await axios.get('/api/assistant/config');
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message, status: err.response?.status };
  }
}

export async function saveAssistantKey(apiKey) {
  try {
    const res = await axios.post('/api/assistant/config', { apiKey });
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message, status: err.response?.status };
  }
}

export async function clearAssistantKey() {
  try {
    const res = await axios.delete('/api/assistant/config');
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message, status: err.response?.status };
  }
}

// ========== Credentials ==========

export async function getCredentials(platform) {
  try {
    const res = await axios.get(`/api/credentials/${platform}`);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function saveCredentials(platform, credentials) {
  try {
    const res = await axios.post(`/api/credentials/${platform}`, credentials);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function testConnection(platform, credentials) {
  try {
    const res = await axios.post(`/api/credentials/${platform}/test`, credentials);
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
}

// ========== Sync ==========

export async function getSyncStatus() {
  try {
    const res = await axios.get('/api/sync/status');
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}

export async function triggerSync(platform) {
  try {
    const res = await axios.post(`/api/sync/${platform}`);
    return res.data;
  } catch (err) {
    return { error: err.response?.data?.error || err.message };
  }
}
